import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { buildYAxis, formatNumber, formatStageDuration, marketBreakdownFromPairs, portfolioMarketBreakdown } from './dashboardFormat'
import { PeriodOption } from './PeriodDropdown'
import { useB2BDashboard } from '../../hooks/useB2BDashboard'
import { useB2BAgencies } from '../../hooks/useB2BAgencies'
import { DashboardCurrency, DashboardRange, MonthlySalesPoint } from '../../domain/entities/B2BDashboard'

type PeriodSelectionId = DashboardRange | 'CURRENT_MONTH' | 'PREVIOUS_MONTH'

const todayISO = () => new Date().toISOString().slice(0, 10)
const startOfYearISO = () => `${new Date().getFullYear()}-01-01`

const pad2 = (value: number) => String(value).padStart(2, '0')
const toISODate = (date: Date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`

const currentMonthRange = () => {
    const now = new Date()
    return { from: toISODate(new Date(now.getFullYear(), now.getMonth(), 1)), to: todayISO() }
}

const previousMonthRange = () => {
    const now = new Date()
    const from = toISODate(new Date(now.getFullYear(), now.getMonth() - 1, 1))
    const to = toISODate(new Date(now.getFullYear(), now.getMonth(), 0))
    return { from, to }
}

const formatMonthLabel = (month: string): string => {
    const [year, m] = month.split('-').map(Number)
    const date = new Date(Date.UTC(year, m - 1, 1))
    const label = date.toLocaleDateString('es-ES', { month: 'short', timeZone: 'UTC' }).replace('.', '')
    const currentYear = new Date().getFullYear()
    return year === currentYear ? label : `${label} ${String(year).slice(-2)}`
}

export function useDashboardOverviewData() {
    const { t } = useTranslation('dashboard')

    const PERIOD_OPTIONS: PeriodOption[] = [
        { id: 'YEAR_TO_DATE', label: t('metrics.periodYearToDate'), range: '' },
        { id: 'LAST_12_MONTHS', label: t('metrics.periodLast12Months'), range: '' },
        { id: 'CURRENT_MONTH', label: t('metrics.periodCurrentMonth'), range: '' },
        { id: 'PREVIOUS_MONTH', label: t('metrics.periodPreviousMonth'), range: '' },
        { id: 'CUSTOM', label: t('metrics.periodCustom'), range: '' }
    ]

    const MONTHLY_BUCKETS = [
        { key: 'es' as const, label: t('metrics.marketSpain'), color: '#E8A200' },
        { key: 'us' as const, label: t('metrics.marketUSA'), color: '#04A3B3' },
        { key: 'fr' as const, label: t('metrics.marketFrance'), color: '#F3A0A8' },
        { key: 'others' as const, label: t('metrics.marketOthers'), color: '#C6C6CE' }
    ]

    const PORTFOLIO_WIDGET_META: Record<string, { label: string; color: string; note: (n: number, d: number) => string }> = {
        packsActivated: {
            label: t('metrics.portfolioPacksActivated'),
            color: '#18AE60',
            note: (n, d) => t('metrics.portfolioPacksActivatedNote', {n: formatNumber(n), d: formatNumber(d)})
        },
        packsInactive: {
            label: t('metrics.portfolioPacksInactive'),
            color: '#E8A200',
            note: (n, d) => t('metrics.portfolioPacksInactiveNote', {n: formatNumber(n), d: formatNumber(d)})
        },
        renewals: {
            label: t('metrics.portfolioRenewals'),
            color: '#04A3B3',
            note: (n, d) => t('metrics.portfolioRenewalsNote', {n: formatNumber(n), d: formatNumber(d)})
        }
    }

    const [selectionId, setSelectionId] = useState<PeriodSelectionId>('YEAR_TO_DATE')
    const [appliedFrom, setAppliedFrom] = useState(startOfYearISO())
    const [appliedTo, setAppliedTo] = useState(todayISO())
    const [draftFrom, setDraftFrom] = useState(appliedFrom)
    const [draftTo, setDraftTo] = useState(appliedTo)
    const [currency, setCurrency] = useState<DashboardCurrency>('EUR')

    const monthPreset = selectionId === 'CURRENT_MONTH' ? currentMonthRange()
        : selectionId === 'PREVIOUS_MONTH' ? previousMonthRange()
        : null

    const queryRange: DashboardRange = selectionId === 'CURRENT_MONTH' || selectionId === 'PREVIOUS_MONTH'
        ? 'CUSTOM'
        : selectionId
    const queryFrom = monthPreset ? monthPreset.from : appliedFrom
    const queryTo = monthPreset ? monthPreset.to : appliedTo

    const { dashboard, isLoadingDashboard } = useB2BDashboard({ range: queryRange, from: queryFrom, to: queryTo, currency })
    const { agencies, isLoadingAgencies } = useB2BAgencies()

    const applyCustomRange = () => {
        setAppliedFrom(draftFrom)
        setAppliedTo(draftTo)
    }

    const money = (value: number) => {
        // useGrouping must be forced 'always': es-ES's 'auto' default uses "min2"
        // grouping, which drops the thousands separator for 4-digit values (e.g. 7829).
        const formatted = value.toLocaleString('es-ES', { maximumFractionDigits: 0, useGrouping: 'always' } as any)
        return currency === 'EUR' ? `${formatted} €` : `$${formatted}`
    }

    const periodOption = PERIOD_OPTIONS.find((o) => o.id === selectionId) ?? PERIOD_OPTIONS[0]
    const periodSelector = {
        id: periodOption.id,
        label: periodOption.id === 'CUSTOM' ? formatCustomRange(appliedFrom, appliedTo) : periodOption.label,
        isCustom: periodOption.id === 'CUSTOM',
        from: draftFrom,
        to: draftTo,
        options: PERIOD_OPTIONS,
        selectedId: periodOption.id,
        select: (id: string) => setSelectionId(id as PeriodSelectionId),
        setFrom: setDraftFrom,
        setTo: setDraftTo,
        onApply: applyCustomRange
    }

    const flowKpis = useMemo(() => {
        if (!dashboard) return []
        const { cards } = dashboard
        return [
            { key: 'printed', label: t('packs.kpiPrinted'), value: formatNumber(cards.printedTripbooks.total), unit: '', note: t('metrics.kpiPrintedNote'), markets: marketBreakdownFromPairs(cards.printedTripbooks.byMarket) },
            { key: 'sold', label: t('metrics.kpiSold'), value: formatNumber(cards.soldTripbooks.total), unit: '', note: t('metrics.kpiSoldNote'), markets: marketBreakdownFromPairs(cards.soldTripbooks.byMarket) },
            { key: 'packs', label: t('metrics.kpiPacksSold'), value: formatNumber(cards.soldPacks.total), unit: '', note: t('metrics.kpiPacksSoldNote'), markets: marketBreakdownFromPairs(cards.soldPacks.byMarket) },
            {
                key: 'avgPackVolume',
                label: t('metrics.kpiAvgPackVolume'),
                value: cards.soldPacks.total ? formatNumber(Math.round(cards.averagePackSize.total)) : '—',
                unit: t('metrics.kpiUnits'),
                note: t('metrics.kpiAvgPackVolumeNote', {a: formatNumber(cards.soldTripbooks.total), b: formatNumber(cards.soldPacks.total)}),
                markets: marketBreakdownFromPairs(cards.averagePackSize.byMarket, (v) => formatNumber(Math.round(v)))
            },
            { key: 'newAgencies', label: t('metrics.kpiNewAgencies'), value: formatNumber(cards.newAgencies.total), unit: '', note: t('metrics.kpiNewAgenciesNote'), markets: marketBreakdownFromPairs(cards.newAgencies.byMarket) },
            { key: 'newRenewals', label: t('metrics.kpiNewRenewals'), value: formatNumber(cards.renewals.total), unit: '', note: t('metrics.kpiNewRenewalsNote'), markets: marketBreakdownFromPairs(cards.renewals.byMarket) }
        ]
    }, [dashboard, t])

    const salesCard = useMemo(() => {
        if (!dashboard) return { total: '—', markets: [], currency, setCurrency }
        return {
            total: money(dashboard.cards.generatedSales.total),
            markets: marketBreakdownFromPairs(dashboard.cards.generatedSales.byMarket, money),
            currency,
            setCurrency
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboard, currency])

    const teamSplit = useMemo(() => ({
        available: dashboard?.cards.tripbookAssembly.available ?? false,
        byMarket: dashboard?.cards.tripbookAssembly.byMarket ?? []
    }), [dashboard])

    const salesChart = useMemo(() => {
        const points: MonthlySalesPoint[] = dashboard?.monthlySales.points ?? []
        const peak = Math.max(...points.map((p) => p.total), 0) || 1
        const yAxis = buildYAxis(peak, money)
        return {
            average: dashboard ? money(dashboard.monthlySales.average) : '—',
            legend: MONTHLY_BUCKETS.map((b) => ({ code: b.key, label: b.label, color: b.color })),
            yAxis,
            months: points.map((point) => ({
                label: formatMonthLabel(point.month),
                total: money(point.total),
                segments: MONTHLY_BUCKETS.map((b) => {
                    const value = point[b.key]
                    return {
                        color: b.color,
                        heightPx: value ? Math.max(2, Math.round((value / yAxis.ceiling) * 176)) : 0,
                        label: `${b.label}: ${money(value)}`
                    }
                })
            }))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboard, currency, t])

    const totals = useMemo(() => {
        const counts = new Map<string, number>()
        agencies.forEach((a) => counts.set(a.market, (counts.get(a.market) ?? 0) + 1))
        const pairs = Array.from(counts, ([market, value]) => ({ market, value }))
        return {
            total: formatNumber(agencies.length),
            note: t('metrics.totalsNote'),
            markets: marketBreakdownFromPairs(pairs)
        }
    }, [agencies, t])

    // Stage medians are scoped to the selected period, same as the rest of the
    // dashboard. The monthly chart isn't: the backend always returns its own
    // trailing window regardless of `range`/`from`/`to`.
    const productionStages = useMemo(() => {
        if (!dashboard) return []
        const { orders } = dashboard.productionTimes
        return dashboard.productionTimes.stages.filter((stage) => stage.key !== 'dispatch').map((stage) => {
            const p50 = formatStageDuration(stage.p50Hours)
            const p90 = stage.p90Hours != null ? formatStageDuration(stage.p90Hours) : null
            return {
                key: stage.key,
                label: stage.label,
                value: p50.value,
                unit: p50.unit,
                note: p90
                    ? t('metrics.stageNoteWithP90', {n: formatNumber(orders), p90value: p90.value, p90unit: p90.unit})
                    : t('metrics.stageNote', {n: formatNumber(orders)}),
                markets: marketBreakdownFromPairs(stage.byMarket, (v) => {
                    const d = formatStageDuration(v)
                    return d.unit ? `${d.value} ${d.unit}` : d.value
                })
            }
        })
    }, [dashboard, t])

    const productionChart = useMemo(() => {
        const points = dashboard?.productionTimes.monthly ?? []
        const peak = Math.max(...points.flatMap((p) => [p.orders, p.delivered]), 0) || 1
        const yAxis = buildYAxis(peak)
        return {
            hasPartial: points.some((p) => p.partial),
            yAxis,
            series: points.map((point) => ({
                label: formatMonthLabel(point.month) + (point.partial ? ' *' : ''),
                created: point.orders,
                printed: point.delivered,
                createdHeightPx: point.orders ? Math.max(3, Math.round((point.orders / yAxis.ceiling) * 124)) : 0,
                printedHeightPx: point.delivered ? Math.max(3, Math.round((point.delivered / yAxis.ceiling) * 124)) : 0
            }))
        }
    }, [dashboard])

    const productionSummary = useMemo(() => {
        if (!dashboard) return null
        const { orders, delivered } = dashboard.productionTimes
        return t('metrics.productionSummary', {orders: formatNumber(orders), delivered: formatNumber(delivered)})
    }, [dashboard, t])

    // Each widget is historical (all-time) — it isn't tied to the period
    // dropdown above.
    const portfolioWidgets = useMemo(() => {
        if (!dashboard) return []
        return dashboard.portfolioHealth.widgets.map((widget) => {
            const meta = PORTFOLIO_WIDGET_META[widget.key] ?? {
                label: widget.key,
                color: '#16161A',
                note: (n: number, d: number) => t('metrics.portfolioFallbackNote', {n: formatNumber(n), d: formatNumber(d)})
            }
            return {
                key: widget.key,
                label: meta.label,
                color: meta.color,
                insufficientSample: widget.insufficientSample,
                value: widget.value != null ? `${Math.round(widget.value * 100)}%` : 'N/D',
                widthPct: widget.value != null ? Math.max(2, Math.round(widget.value * 100)) : 0,
                note: widget.insufficientSample
                    ? t('metrics.portfolioInsufficientSample', {n: formatNumber(widget.numerator), d: formatNumber(widget.denominator)})
                    : meta.note(widget.numerator, widget.denominator),
                markets: portfolioMarketBreakdown(widget.byMarket)
            }
        })
    }, [dashboard, t])

    return {
        isLoading: isLoadingDashboard || isLoadingAgencies,
        periodSelector,
        flowKpis,
        salesCard,
        teamSplit,
        salesChart,
        totals,
        productionStages,
        productionChart,
        productionSummary,
        portfolioWidgets
    }
}

function formatCustomRange(from: string, to: string): string {
    const MONTH_NAMES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic']
    const format = (iso: string) => {
        if (!iso) return '—'
        const [year, month, day] = iso.split('-')
        return `${parseInt(day, 10)} ${MONTH_NAMES[parseInt(month, 10) - 1]} ${year}`
    }
    return `${format(from)} – ${format(to)}`
}
